// Auswahl mit Suche (einfach oder mehrfach): Status, Auswahlfelder, Nutzer, Kontakte.
// „nummeriert“: Optionen mit 1., 2., … wie in Close (außer der Text beginnt schon mit einer Nummer); bei leerer Suche
// wählt eine Ziffer direkt.
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Ausklapp } from "./Ausklapp";
import { Icon } from "./Icon";

export interface Option {
  wert: string;
  label: string;
  /** Close-Farbname (Statuspunkt) */
  farbe?: string | null;
  unter?: string | null;
  deaktiviert?: boolean;
}

interface Props {
  optionen: Option[];
  wert: string | string[] | null;
  mehrfach?: boolean;
  onWahl: (wert: string | string[] | null) => void;
  platzhalter?: string;
  /** „Leeren“ anbieten */
  leerErlaubt?: boolean;
  deaktiviert?: boolean;
  titel?: string;
  knopfKlasse?: string;
  /** eigener Knopfinhalt (z. B. Status-Pille) */
  knopfInhalt?: ReactNode;
  breite?: number | "anker";
  /** sofort öffnen (Inline-Bearbeitung) */
  autoOeffnen?: boolean;
  onGeschlossen?: () => void;
  id?: string;
  /** Optionen nummerieren (1., 2., …); Ziffer 1–9 wählt direkt, solange die Suche leer ist */
  nummeriert?: boolean;
  /** Pflichtfeld (für Screenreader) */
  pflicht?: boolean;
}

const normal = (s: string) => s.toLocaleLowerCase("de").normalize("NFD").replace(/[̀-ͯ]/g, "");

export function Auswahl({
  optionen,
  wert,
  mehrfach = false,
  onWahl,
  platzhalter = "Auswählen …",
  leerErlaubt = false,
  deaktiviert = false,
  titel,
  knopfKlasse = "auswahl__knopf",
  knopfInhalt,
  breite = "anker",
  autoOeffnen = false,
  onGeschlossen,
  id,
  nummeriert = false,
  pflicht = false,
}: Props) {
  const [offen, setOffen] = useState(autoOeffnen);
  const [suche, setSuche] = useState("");
  const [aktiv, setAktiv] = useState(0);
  const [knopf, setKnopf] = useState<HTMLButtonElement | null>(null);
  const sucheRef = useRef<HTMLInputElement>(null);
  const listeRef = useRef<HTMLUListElement>(null);
  const gewaehlt = useMemo(() => new Set(Array.isArray(wert) ? wert : wert ? [wert] : []), [wert]);
  const mitSuche = optionen.length > 6;

  const gefiltert = useMemo(() => {
    const n = normal(suche.trim());
    return n ? optionen.filter((o) => normal(o.label).includes(n) || normal(o.unter ?? "").includes(n)) : optionen;
  }, [optionen, suche]);

  useEffect(() => {
    if (!offen) return;
    setSuche("");
    const i = optionen.findIndex((o) => gewaehlt.has(o.wert));
    setAktiv(i >= 0 ? i : 0);
    window.setTimeout(() => (mitSuche ? sucheRef.current?.focus() : listeRef.current?.focus()), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offen]);

  useEffect(() => {
    listeRef.current?.querySelector<HTMLElement>(`[data-index="${aktiv}"]`)?.scrollIntoView({ block: "nearest" });
  }, [aktiv]);

  /** fokusZurueck: nach einer Wahl per Tastatur/Klick zurück auf den Knopf, damit Tab zum nächsten Feld geht */
  const schliessen = (fokusZurueck = false) => {
    setOffen(false);
    onGeschlossen?.();
    if (fokusZurueck && knopf?.isConnected) knopf.focus({ preventScroll: true });
  };

  const waehlen = (o: Option) => {
    if (o.deaktiviert) return;
    if (mehrfach) {
      const neu = new Set(gewaehlt);
      if (neu.has(o.wert)) neu.delete(o.wert);
      else neu.add(o.wert);
      onWahl(optionen.filter((x) => neu.has(x.wert)).map((x) => x.wert).concat([...neu].filter((w) => !optionen.some((x) => x.wert === w))));
    } else {
      onWahl(o.wert);
      schliessen(true);
    }
  };

  const taste = (e: React.KeyboardEvent) => {
    if (nummeriert && !suche && /^[1-9]$/.test(e.key) && !e.metaKey && !e.ctrlKey && !e.altKey) {
      const o = optionen[Number(e.key) - 1];
      if (o) {
        e.preventDefault();
        setAktiv(Number(e.key) - 1);
        waehlen(o);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setAktiv((a) => Math.min(a + 1, gefiltert.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setAktiv((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const o = gefiltert[aktiv];
      if (o) waehlen(o);
      else if (mehrfach) schliessen(true);
    } else if (e.key === "Tab") {
      // Fokus erst auf den Knopf, dann springt der Browser von dort zum nächsten Feld
      schliessen(true);
    }
  };

  const gewaehlteOptionen = optionen.filter((o) => gewaehlt.has(o.wert));
  const fremde = [...gewaehlt].filter((w) => !optionen.some((o) => o.wert === w));
  const anzeige =
    knopfInhalt ??
    (gewaehlteOptionen.length || fremde.length ? (
      <span className="auswahl__wert">
        {gewaehlteOptionen.map((o) => (
          <span key={o.wert} className={mehrfach ? "chip" : "auswahl__einzel"}>
            {o.farbe !== undefined ? <span className={`punkt punkt--${o.farbe ?? "gray"}`} /> : null}
            {o.label}
          </span>
        ))}
        {fremde.map((w) => (
          <span key={w} className={mehrfach ? "chip" : "auswahl__einzel"}>
            {w}
          </span>
        ))}
      </span>
    ) : (
      <span className="auswahl__platzhalter">{platzhalter}</span>
    ));

  return (
    <>
      <button
        ref={setKnopf}
        id={id}
        type="button"
        className={knopfKlasse}
        disabled={deaktiviert}
        title={titel}
        aria-haspopup="listbox"
        aria-expanded={offen}
        aria-required={pflicht || undefined}
        onClick={(e) => {
          e.stopPropagation();
          if (offen) schliessen(true);
          else setOffen(true);
        }}
        onKeyDown={(e) => {
          if (!offen && (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setOffen(true);
          }
        }}
      >
        {anzeige}
        {knopfInhalt ? null : <Icon name="chevron-unten" groesse={14} className="auswahl__pfeil" />}
      </button>
      <Ausklapp anker={knopf} offen={offen} onSchliessen={() => schliessen()} breite={breite} minBreite={220} rolle="listbox">
        <div className="auswahl" onKeyDown={taste}>
          {mitSuche ? (
            <div className="auswahl__suche">
              <Icon name="suche" groesse={14} />
              <input
                ref={sucheRef}
                value={suche}
                placeholder="Suchen …"
                onChange={(e) => {
                  setSuche(e.target.value);
                  setAktiv(0);
                }}
              />
            </div>
          ) : null}
          <ul ref={listeRef} className="auswahl__liste" tabIndex={-1} role="listbox" aria-multiselectable={mehrfach}>
            {gefiltert.length === 0 ? <li className="auswahl__leer">Keine Treffer.</li> : null}
            {gefiltert.map((o, i) => {
              const an = gewaehlt.has(o.wert);
              return (
                <li
                  key={o.wert}
                  data-index={i}
                  role="option"
                  aria-selected={an}
                  aria-disabled={o.deaktiviert}
                  className={`auswahl__option${i === aktiv ? " auswahl__option--aktiv" : ""}${an ? " auswahl__option--an" : ""}${o.deaktiviert ? " auswahl__option--aus" : ""}`}
                  onMouseEnter={() => setAktiv(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => waehlen(o)}
                >
                  {mehrfach ? <span className={`kasten${an ? " kasten--an" : ""}`}>{an ? <Icon name="haken" groesse={12} /> : null}</span> : null}
                  {o.farbe !== undefined ? <span className={`punkt punkt--${o.farbe ?? "gray"}`} /> : null}
                  {nummeriert && !/^\s*\d+\s*[.)]/.test(o.label) ? <span className="auswahl__nr">{optionen.indexOf(o) + 1}.</span> : null}
                  <span className="auswahl__label">
                    {o.label}
                    {o.unter ? <span className="auswahl__unter">{o.unter}</span> : null}
                  </span>
                  {!mehrfach && an ? <Icon name="haken" groesse={14} className="auswahl__haken" /> : null}
                </li>
              );
            })}
          </ul>
          {leerErlaubt || mehrfach ? (
            <div className="auswahl__fuss">
              {leerErlaubt ? (
                <button
                  type="button"
                  className="knopf knopf--klein knopf--leise"
                  disabled={gewaehlt.size === 0}
                  onClick={() => {
                    onWahl(mehrfach ? [] : null);
                    if (!mehrfach) schliessen(true);
                  }}
                >
                  Leeren
                </button>
              ) : (
                <span />
              )}
              {mehrfach ? (
                <button type="button" className="knopf knopf--klein knopf--primaer" onClick={() => schliessen(true)}>
                  Fertig
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
      </Ausklapp>
    </>
  );
}
